/**
 * @fileoverview Archivo de utilidades para los jugadores
 */

/**
 * Obtiene la categoria de un jugador en la temporada actual segun su año de nacimiento.
 */
export function obtenerCategoria(anoNacimiento) {
  const currentTime = new Date();

  // A partir de septiembre, cogemos el año siguiente
  const year = currentTime.getMonth() > 8 ? currentTime.getFullYear() + 1 : currentTime.getFullYear();
  const edad = year - anoNacimiento;

  switch (edad) {
    case 19:
      return 'Juvenil 3';
    case 18:
      return 'Juvenil 2';
    case 17:
      return 'Juvenil 1';
    case 16:
      return 'Cadete 2';
    case 15:
      return 'Cadete 1';
    case 14:
      return 'Infantil 2';
    case 13:
      return 'Infantil 1';
    case 12:
      return 'Alevin 2';
    case 11:
      return 'Alevin 1';
    case 10:
      return 'Benjam. 2';
    case 9:
      return 'Benjam. 1';
    case 8:
      return 'Prebenj. 2';
    case 7:
      return 'Prebenj. 1';
    case 6:
    case 5:
    case 4:
    case 3:
      return 'Biber.';
    default:
      return 'Senior';
  }
}

/**
 * Obtiene el codigo de la categoria de un jugador. Ejemplo: Cadete de primer año -> C1
 */
export function obtenerCategoriaCodigo(anoTemporada, anoNacimiento) {
  const edad = anoTemporada - anoNacimiento;
  switch (edad) {
    case 19:
      return 'J3';
    case 18:
      return 'J2';
    case 17:
      return 'J1';
    case 16:
      return 'C2';
    case 15:
      return 'C1';
    case 14:
      return 'I2';
    case 13:
      return 'I1';
    case 12:
      return 'A2';
    case 11:
      return 'A1';
    case 10:
      return 'B2';
    case 9:
      return 'B1';
    case 8:
      return 'P2';
    case 7:
      return 'P1';
    case 6:
    case 5:
    case 4:
    case 3:
      return 'BIB';
    default:
      return 'S';
  }
}

/**
 * Obtiene la categoría estimada (nombre descriptivo, código y grupo) para un año de nacimiento y temporada.
 */
export function obtenerCategoriaEstimada(anoNacimiento, temporada) {
  if (!anoNacimiento) {
    return null;
  }
  let anoFin;
  if (temporada && typeof temporada === 'string' && temporada.includes('-')) {
    anoFin = parseInt(temporada.split('-')[1], 10);
  } else {
    const now = new Date();
    anoFin = now.getMonth() >= 7 ? now.getFullYear() + 1 : now.getFullYear();
  }

  const edad = anoFin - anoNacimiento;
  switch (edad) {
    case 19:
      return { nombre: 'Juvenil 3º año', codigo: 'J3', grupo: 'JUVENIL' };
    case 18:
      return { nombre: 'Juvenil 2º año', codigo: 'J2', grupo: 'JUVENIL' };
    case 17:
      return { nombre: 'Juvenil 1º año', codigo: 'J1', grupo: 'JUVENIL' };
    case 16:
      return { nombre: 'Cadete 2º año', codigo: 'C2', grupo: 'CADETE' };
    case 15:
      return { nombre: 'Cadete 1º año', codigo: 'C1', grupo: 'CADETE' };
    case 14:
      return { nombre: 'Infantil 2º año', codigo: 'I2', grupo: 'INFANTIL' };
    case 13:
      return { nombre: 'Infantil 1º año', codigo: 'I1', grupo: 'INFANTIL' };
    case 12:
      return { nombre: 'Alevín 2º año', codigo: 'A2', grupo: 'ALEVIN' };
    case 11:
      return { nombre: 'Alevín 1º año', codigo: 'A1', grupo: 'ALEVIN' };
    case 10:
      return { nombre: 'Benjamín 2º año', codigo: 'B2', grupo: 'BENJAMIN' };
    case 9:
      return { nombre: 'Benjamín 1º año', codigo: 'B1', grupo: 'BENJAMIN' };
    case 8:
      return { nombre: 'Prebenjamín 2º año', codigo: 'P2', grupo: 'PREBENJAMIN' };
    case 7:
      return { nombre: 'Prebenjamín 1º año', codigo: 'P1', grupo: 'PREBENJAMIN' };
    case 6:
    case 5:
    case 4:
    case 3:
      return { nombre: 'Biberón', codigo: 'BIB', grupo: 'BIBERON' };
    default:
      return edad > 19 ? { nombre: 'Senior', codigo: 'S', grupo: 'SENIOR' } : null;
  }
}

/**
 * Comprueba si un jugador en un año dado tiene edad para una categoría concreta.
 * Retorna true si tiene edad para la categoría, false si la supera.
 * Se asume que categorías inferiores pueden jugar en superiores, solo se restringe si superan la edad.
 */
export function jugadorAptoParaCategoria(anoTemporada, anoNacimiento, categoriaEquipo) {
  if (!anoNacimiento) {
    return true;
  } // Si no hay año de nacimiento, no filtramos

  const maxEdadPorCategoria = {
    Sénior: 99,
    Seniors: 99,
    'Sub 23': 23,
    Juveniles: 19,
    Juvenil: 19,
    Cadetes: 16,
    Cadete: 16,
    Infantiles: 14,
    Infantil: 14,
    Alevines: 12,
    Alevin: 12,
    Benjamines: 10,
    Benjamin: 10,
    Prebenjamines: 8,
    Prebenjamin: 8,
    Biberones: 6,
    Biberon: 6,
    Aficionado: 99,
  };

  const edadEnTemporada = anoTemporada - anoNacimiento;

  // Buscar coincidencia en las claves de categorias (ignorar tildes)
  const normCat = categoriaEquipo.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  let maxEdad = 99; // Por defecto sin límite

  for (const [key, value] of Object.entries(maxEdadPorCategoria)) {
    const normKey = key.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (normCat.toLowerCase().includes(normKey.toLowerCase())) {
      maxEdad = value;
      break;
    }
  }

  return edadEnTemporada <= maxEdad;
}

/**
 * Obtiene el color de un jugador en una lista de jugadores segun su categoria.
 */
export function obtenerColorCategoria(cat) {
  if (!cat) {
    return '#000000';
  }
  const categoria = cat.split(' ')[0];
  switch (categoria) {
    case 'SENIOR':
      return '#fef4f4ff';
    case 'JUVENILES':
    case 'JUVENIL':
      return '#fff4ecff';
    case 'CADETES':
    case 'CADETE':
      return '#fffdf3ff';
    case 'INFANTILES':
    case 'INFANTIL':
      return '#f0fbeaff';
    case 'ALEVINES':
    case 'ALEVIN':
    case 'ALEVÍN':
      return '#edfbf5ff';
    case 'BENJAMINES':
    case 'BENJAMIN':
    case 'BENJAMÍN':
      return '#effbffff';
    case 'PREBENJAMINES':
    case 'PREBENJAMIN':
    case 'PREBENJAMÍN':
      return '#d2d9fdff';
    case 'BIBERONES':
    case 'BIBERON':
    case 'BIBERÓN':
      return '#e9d2fdff';
    default:
      return '#FFFFFF';
  }
}

/**
 * Obtiene el estado de un jugador en una categoria segun su año (primer año, segundo año, promocionado)
 */
export function obtenerEstadoJugadorEnCategoria(codigoCategoriaJugador, catEquipo) {
  const mapCategoria = {
    SENIOR: { S: 3, J3: -1, J2: -1, J1: -1, C2: -2, C1: -2 },
    JUVENILES: { J3: 3, J2: 2, J1: 1, C2: -1, C1: -2 },
    JUVENIL: { J3: 3, J2: 2, J1: 1, C2: -1, C1: -2 },
    CADETES: { C2: 3, C1: 2, I2: -1, I1: -2 },
    CADETE: { C2: 3, C1: 2, I2: -1, I1: -2 },
    INFANTILES: { I2: 3, I1: 2, A2: -1, A1: -2 },
    INFANTIL: { I2: 3, I1: 2, A2: -1, A1: -2 },
    ALEVINES: { A2: 3, A1: 2, B2: -1, B1: -2 },
    ALEVIN: { A2: 3, A1: 2, B2: -1, B1: -2 },
    ALEVÍN: { A2: 3, A1: 2, B2: -1, B1: -2 },
    BENJAMINES: { B2: 3, B1: 2, P2: -1, P1: -2 },
    BENJAMIN: { B2: 3, B1: 2, P2: -1, P1: -2 },
    BENJAMÍN: { B2: 3, B1: 2, P2: -1, P1: -2 },
    PREBENJAMINES: { P2: 3, P1: 2 },
    PREBENJAMIN: { P2: 3, P1: 2 },
    PREBENJAMÍN: { P2: 3, P1: 2 },
  };

  const categoriaEquipo = catEquipo.split(' ')[0];
  return mapCategoria[categoriaEquipo]?.[codigoCategoriaJugador] ?? -3;
}

/**
 * Trunca un numero a un numero de decimales.
 */
function truncate(num, places) {
  return Math.trunc(num * Math.pow(10, places)) / Math.pow(10, places);
}

/**
 * Obtiene el porcentaje de titularidades de un jugador.
 */
export function obtenerPorcentajeTitular(convocados, titularidades) {
  return truncate(titularidades / convocados, 2);
}

/**
 * Obtiene la paleta de colores (fondo, texto, borde) según la nota (1-10) del jugador.
 * Escala cromática intuitiva:
 * - 10: Verde oscuro
 * - 9 y 9.5: Verde normal
 * - 8 y 8.5: Verde clarito
 * - 7 y 7.5: Amarillo casi verde
 * - 6 y 6.5: Naranja amarillo
 * - 5 y 5.5: Naranja más intenso
 * - 3.5 al 4.5: Rojo
 * - 1 al 3: Rojo más oscuro
 */
export function obtenerColorNota(nota) {
  if (nota === null || nota === undefined || nota === '') {
    return {
      bg: '#f1f5f9',
      color: '#64748b',
      border: '#cbd5e1',
      dark: '#475569',
    };
  }
  const n = Number(nota);
  if (n >= 9.75) {
    return { bg: '#86efac', color: '#052e16', border: '#166534', dark: '#052e16' };
  }
  if (n >= 8.75) {
    return { bg: '#bbf7d0', color: '#166534', border: '#4ade80', dark: '#15803d' };
  }
  if (n >= 7.75) {
    return { bg: '#dcfce7', color: '#15803d', border: '#86efac', dark: '#16a34a' };
  }
  if (n >= 6.75) {
    return { bg: '#ecfccb', color: '#4d7c0f', border: '#bef264', dark: '#65a30d' };
  }
  if (n >= 5.75) {
    return { bg: '#fef3c7', color: '#b45309', border: '#fcd34d', dark: '#d97706' };
  }
  if (n >= 4.75) {
    return { bg: '#ffedd5', color: '#c2410c', border: '#fb923c', dark: '#ea580c' };
  }
  if (n >= 3.25) {
    return { bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5', dark: '#dc2626' };
  }
  return { bg: '#fda4af', color: '#4c0519', border: '#f43f5e', dark: '#881337' };
}
